import axios from "axios";
import i18n from "@/lib/i18n";
import type { Ref } from "vue";
import type { I18n } from "vue-i18n";
import { useChangeCurrentUserLocale } from "@generated/tenant/query/tenant";
import { setLocaleCookie } from "@/lib/cookie.ts";

const supportedLocales = { pl: "Polski", en: "English", ua: "Український" };

export type Locale = keyof typeof supportedLocales;

type LocaleChangeHandler = (applyLocale: () => void, fromLocale: Locale, toLocale: Locale) => void;

let localeChangeHandler: LocaleChangeHandler | null = null;

// The new UI shell registers its language switch overlay here; without it the locale changes at once.
export const setLocaleChangeHandler = (handler: LocaleChangeHandler | null): void => {
  localeChangeHandler = handler;
};

export function useLocale(shouldMutateAsync: boolean = true) {
  const { mutateAsync } = useChangeCurrentUserLocale();

  const changeLocale = async (newLocale: Locale) => {
    changeLocaleLocally(newLocale);

    if (shouldMutateAsync) {
      await mutateAsync({ data: { locale: newLocale } });
    }
  };

  const changeLocaleLocally = (newLocale: Locale) => {
    const localeRef = (i18n as I18n).global.locale as unknown as Ref<Locale>;

    const currentLocale = localeRef.value;

    const applyLocale = (): void => {
      setLocaleCookie(newLocale);

      localeRef.value = newLocale;
      axios.defaults.headers.common["X-User-Locale"] = newLocale;

      document.querySelector("html")?.setAttribute("lang", newLocale);
    };

    if (localeChangeHandler && currentLocale !== newLocale) {
      localeChangeHandler(applyLocale, currentLocale, newLocale);

      return;
    }

    applyLocale();
  };

  return {
    locale: (i18n as I18n).global.locale,
    changeLocale,
    supportedLocales,
    changeLocaleLocally,
  };
}
